/**
 * Date Helper Utilities
 */
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

dayjs.extend(customParseFormat);
dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

/**
 * Check if two date ranges overlap
 */
export const dateRangesOverlap = (start1, end1, start2, end2) => {
  const d1 = dayjs(start1);
  const d2 = dayjs(end1);
  const d3 = dayjs(start2);
  const d4 = dayjs(end2);
  
  // Two ranges overlap if:
  // Range 1 start is before or equal to Range 2 end AND Range 1 end is after or equal to Range 2 start
  return d1.isSameOrBefore(d4, 'day') && d2.isSameOrAfter(d3, 'day');
};

/**
 * Get consecutive days between two dates (inclusive)
 */
export const getConsecutiveDays = (startDate, endDate) => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  return end.diff(start, 'day') + 1;
};

/**
 * Check if date is within range (inclusive)
 */
export const isDateInRange = (date, startDate, endDate) => {
  const d = dayjs(date);
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  return d.isBetween(start, end, 'day', '[]');
};

/**
 * Format date for display
 */
export const formatDate = (date, format = 'YYYY-MM-DD') => {
  return dayjs(date).format(format);
};

/**
 * Get all dates in range (inclusive)
 */
export const getDatesInRange = (startDate, endDate) => {
  const dates = [];
  let current = dayjs(startDate);
  const end = dayjs(endDate);
  
  while (current.isSameOrBefore(end, 'day')) {
    dates.push(current.format('YYYY-MM-DD'));
    current = current.add(1, 'day');
  }
  
  return dates;
};

/**
 * Check if date is a weekend
 */
export const isWeekend = (date) => {
  const day = dayjs(date).day();
  return day === 0 || day === 6;
};

/**
 * Get working days (Mon-Fri) between two dates (inclusive)
 */
export const getWorkingDays = (startDate, endDate) => {
  let count = 0;
  let current = dayjs(startDate);
  const end = dayjs(endDate);
  
  while (current.isSameOrBefore(end, 'day')) {
    const dayOfWeek = current.day();
    // Count only Monday (1) to Friday (5), exclude Saturday (6) and Sunday (0)
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      count++;
    }
    current = current.add(1, 'day');
  }
  
  return count;
};

/**
 * Get start and end of month
 */
export const getMonthRange = (date) => {
  const d = dayjs(date);
  return {
    start: d.startOf('month').format('YYYY-MM-DD'),
    end: d.endOf('month').format('YYYY-MM-DD'),
  };
};

/**
 * Get start and end of year
 */
export const getYearRange = (date) => {
  const d = dayjs(date);
  return {
    start: d.startOf('year').format('YYYY-MM-DD'),
    end: d.endOf('year').format('YYYY-MM-DD'),
  };
};

